<template>
  <div class="app-container">
    <nav class="navbar">
      <div class="nav-brand">
        <span class="brand-icon">🧬</span>
        <span class="brand-text">Mito-interpreter</span>
      </div>
      <div class="nav-select">
        <button class="usage-btn" @click="openUsage">How to use</button>
        <div class="search-select-wrapper">
          <div class="search-input-wrapper">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search variant" 
              @focus="showDropdown = true"
              @input="filterPresets"
              class="search-input"
            />
            <div class="reference-tooltip">
              <span>Reference: </span>
              <a href="https://clinicalgenome.org/" target="_blank" class="reference-link">https://clinicalgenome.org/</a>
            </div>
          </div>
          <div v-if="showDropdown && filteredPresets.length > 0" class="dropdown-menu">
            <div 
              v-for="preset in filteredPresets" 
              :key="preset.position"
              @click="selectPreset(preset, originalIndex(preset))"
              class="dropdown-item"
              :class="{ active: selectedPreset === originalIndex(preset) }"
            >
              {{ preset.position }}
            </div>
          </div>
        </div>
      </div>
    </nav>
    <main class="main-content">
      <router-view :preset="currentPreset" />
    </main>

    <div class="usage-modal-overlay" v-if="showUsageModal" @click.self="closeUsage">
      <div class="usage-modal">
        <div class="usage-modal-header">
          <span class="usage-modal-title">How to Use Mito-interpreter</span>
          <button class="usage-modal-close" @click="closeUsage">&times;</button>
        </div>
        <div class="usage-modal-body">
          <div class="usage-content" v-html="usageHtml"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { marked } from 'marked'

export default {
  name: 'App',
  data() {
    return {
      presets: [],
      selectedPreset: -1,
      searchQuery: '',
      showDropdown: false,
      filteredPresets: [],
      showUsageModal: false,
      usageHtml: ''
    }
  },
  computed: {
    currentPreset() {
      if (this.selectedPreset < 0) return null
      return this.presets[this.selectedPreset]
    }
  },
  mounted() {
    this.fetchPresets()
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    async fetchPresets() {
      try {
        const response = await axios.get('/api/presets')
        this.presets = response.data
        this.filteredPresets = response.data
      } catch (error) {
        console.error('Error fetching presets:', error)
      }
    },
    filterPresets() {
      const query = this.searchQuery.toLowerCase().trim()
      if (!query) {
        this.filteredPresets = this.presets
        return
      }
      this.filteredPresets = this.presets.filter(preset => 
        preset.position.toLowerCase().includes(query) ||
        preset.assertion.toLowerCase().includes(query)
      )
    },
    selectPreset(preset, index) {
      this.selectedPreset = index
      this.searchQuery = preset.position
      this.showDropdown = false
    },
    originalIndex(preset) {
      return this.presets.findIndex(p => p.position === preset.position)
    },
    handleClickOutside(event) {
      const wrapper = document.querySelector('.search-select-wrapper')
      if (wrapper && !wrapper.contains(event.target)) {
        this.showDropdown = false
      }
    },
    async openUsage() {
      if (!this.usageHtml) {
        try {
          const response = await axios.get('/api/usage')
          this.usageHtml = marked.parse(response.data.content)
        } catch (error) {
          console.error('Error fetching usage:', error)
          this.usageHtml = '<p>Failed to load usage instructions.</p>'
        }
      }
      this.showUsageModal = true
    },
    closeUsage() {
      this.showUsageModal = false
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  min-height: 100vh;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.navbar {
  background: rgba(15, 52, 96, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 9998;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.brand-icon {
  font-size: 1.8rem;
  filter: drop-shadow(0 0 10px rgba(0, 255, 200, 0.5));
}

.brand-text {
  font-size: 1.3rem;
  font-weight: 600;
  background: linear-gradient(135deg, #00d4ff 0%, #7b2cbf 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-select {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.usage-btn {
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: #e0e0e0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.usage-btn:hover {
  background: rgba(0, 212, 255, 0.12);
  border-color: rgba(0, 212, 255, 0.4);
  color: #00d4ff;
}

.usage-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  backdrop-filter: blur(4px);
}

.usage-modal {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.usage-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.usage-modal-title {
  color: #fff;
  font-size: 1.2rem;
  font-weight: 600;
  background: linear-gradient(135deg, #00d4ff 0%, #7b2cbf 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.usage-modal-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.8rem;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
}

.usage-modal-close:hover {
  color: #fff;
}

.usage-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.usage-content {
  color: rgba(255, 255, 255, 0.85);
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: 0.88rem;
  line-height: 1.75;
}

.usage-content h1,
.usage-content h2,
.usage-content h3 {
  color: #00d4ff;
  margin-top: 0.8rem;
  margin-bottom: 0.4rem;
  font-weight: 600;
}

.usage-content h1:first-child,
.usage-content h2:first-child,
.usage-content h3:first-child {
  margin-top: 0;
}

.usage-content h1 { font-size: 1.4rem; }
.usage-content h2 { font-size: 1.15rem; }
.usage-content h3 { font-size: 1rem; }

.usage-content p {
  margin-bottom: 0.7rem;
}

.usage-content ul,
.usage-content ol {
  margin: 0.4rem 0 0.7rem 1.5rem;
}

.usage-content li {
  margin-bottom: 0.3rem;
}

.usage-content code {
  background: rgba(0, 212, 255, 0.12);
  color: #00d4ff;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.85em;
}

.usage-content pre {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.8rem 1rem;
  overflow-x: auto;
  margin: 0.6rem 0;
}

.usage-content pre code {
  background: none;
  color: rgba(255, 255, 255, 0.9);
  padding: 0;
}

.usage-content strong {
  color: #fff;
  font-weight: 600;
}

.usage-content a {
  color: #00d4ff;
  text-decoration: none;
}

.usage-content a:hover {
  text-decoration: underline;
}

.usage-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.8rem 0;
  font-size: 0.82rem;
}

.usage-content th,
.usage-content td {
  border: 1px solid rgba(255, 255, 255, 0.15);
  padding: 0.5rem 0.7rem;
  text-align: left;
}

.usage-content th {
  background: rgba(0, 212, 255, 0.12);
  color: #00d4ff;
  font-weight: 600;
}

.usage-content hr {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 1rem 0;
}

.usage-content blockquote {
  border-left: 3px solid rgba(0, 212, 255, 0.4);
  margin: 0.6rem 0;
  padding: 0.3rem 1rem;
  color: rgba(255, 255, 255, 0.7);
}

.search-select-wrapper {
  position: relative;
  width: 300px;
}

.search-input-wrapper {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #e0e0e0;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.search-input:hover, .search-input:focus {
  outline: none;
  background: rgba(0, 212, 255, 0.15);
  border-color: rgba(0, 212, 255, 0.4);
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.reference-tooltip {
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%) translateX(-12px);
  background: rgba(0, 0, 0, 0.9);
  color: #e0e0e0;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 10000;
  pointer-events: auto;
}

.search-input-wrapper:hover .reference-tooltip {
  opacity: 1;
  visibility: visible;
}

.reference-link {
  color: #00d4ff;
  text-decoration: none;
  transition: color 0.3s ease;
}

.reference-link:hover {
  color: #7b2cbf;
  text-decoration: underline;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: rgba(26, 26, 46);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 9999;
}

.dropdown-item {
  padding: 0.6rem 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
  color: #e0e0e0;
}

.dropdown-item:hover, .dropdown-item.active {
  background: rgba(0, 212, 255, 0.15);
}

.nav-links {
  display: flex;
  gap: 1rem;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  text-decoration: none;
  color: #a0a0a0;
  font-weight: 500;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-link:hover {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
  border-color: rgba(0, 212, 255, 0.3);
}

.nav-link.active {
  background: linear-gradient(135deg, #00d4ff 0%, #7b2cbf 100%);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
}

.link-icon {
  font-size: 1.1rem;
}

.main-content {
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

@media (max-width: 768px) {
  .navbar {
    flex-direction: column;
    gap: 1rem;
  }
  
  .nav-links {
    width: 100%;
    justify-content: center;
  }
}
</style>
